/**
 * Regulile pure ale autentificarii cu cod (U2): casetele de cod si pauza dintre
 * doua cereri.
 *
 * Modulul NU stie nimic despre React si nu face niciun apel — de-aia se poate
 * testa fara sa monteze nimic, ca `video-coverage.ts`.
 *
 * 🔴 CUSATURA, ca sa nu se piarda: ce scrie omul e un IDENTIFICATOR — email sau
 * telefon — si dupa el se cauta contul; codul pleaca pe canalul identificatorului
 * introdus (email → email, telefon → mesagerie). Ecranele NU numesc canalul:
 * singurul care-l spune e mesajul serverului, randat ca atare — asa, o schimbare de
 * canal nu cere niciun deploy de frontend si nu lasa in urma un text care minte.
 *
 * 🔴 Corectat la T1 (SP-01): `/api/auth/otp/verify` intoarce acum SESIUNEA, corpul
 * lui `/api/auth/login`. Caseta asta spunea, pana atunci, exact invers — ca fluxul
 * „recupereaza accesul" si se termina cu „alege o parola noua" — si era scrisa ca un
 * adevar pinuit. Asa arata o cusatura care imbatraneste: corecta cand s-a scris,
 * falsa dupa o schimbare de contract, si tacuta intre cele doua.
 *
 * 🔴 CONSTANTE OGLINDITE DIN BACKEND — una singura, si spusa pe fata. Pana pe 2026-10-08 regula de
 * aici era „niciuna": `/verify` avea un singur raspuns de esec, deci ecranul n-avea de unde sti ca
 * un cod a expirat si nu afirma nimic. De atunci `/verify` trimite `motiv` (`COD_EXPIRAT`,
 * `COD_FOLOSIT`, `COD_INVALID` — vezi `otp-mesaje.ts`), deci „a expirat" se spune DOAR cand o spune
 * serverul, niciodata dintr-un ceas propriu.
 * Ce se oglindeste e `auth.otp.ttl-minutes` (10), in `TTL_COD_MINUTE`, pentru doua lucruri: textul
 * „orice cod primit in ultimele 10 minute" si cat se tine minte ecranul dupa o reincarcare
 * (`otp-stare-pastrata.ts`). Valoarea NU se expune prin API, deci poate diverge tacut; directia
 * greselii e inofensiva — un text aproximativ, respectiv un ecran restaurat pe care serverul
 * raspunde oricum cu motivul corect. `auth.otp.max-requests-per-15-min` ramane NEoglindit: limita
 * o spune serverul (429 `LIMITA_COD`, cu `secundePanaLaCodNou`).
 */

/**
 * Identificatorul introdus la cererea codului: DOAR EMAIL (decizie client,
 * 2026-09-17). Un telefon acceptat aici ar cadea pe canalul WhatsApp in backend
 * (`OtpService.gasesteTinta`), oprit pe PROD (`WHATSAPP_ENABLED` nesetat) — codul
 * n-ar pleca niciodata, iar raspunsul uniform (anti-enumerare) ar arata acelasi
 * succes ca la un email valid. De-aia respingerea telefonului se face AICI, pe
 * client, inainte de orice cerere in retea, nu doar lasata pe seama backendului.
 */
export function esteEmailValid(text: string): boolean {
    const t = String(text ?? '').trim();
    return /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(t);
}

/** Codurile au exact 6 cifre (`OtpService`: `String.format("%06d", ...)`). */
export const LUNGIME_COD = 6;

/** Cat e valabil un cod, in minute — oglinda lui `auth.otp.ttl-minutes` (vezi caseta de sus). */
export const TTL_COD_MINUTE = 10;

/**
 * Pauza dintre doua cereri. E ALEGEREA NOASTRA, nu o oglindire: backendul nu
 * impune o pauza pe cerere, iar daca ne inselam in vreun sens, greseala e
 * inofensiva — omul cere codul nou putin mai devreme sau mai tarziu, nimic nu se
 * deblocheaza.
 */
export const PAUZA_SECUNDE = 60;

/** Peste atat, o asteptare „spusa de server" e considerata valoare stricata si se ignora. */
const ASTEPTARE_MAXIMA_SECUNDE = 3600;

export interface StareRetrimitere {
    poateCere: boolean;
    secundeRamase: number;
}

/** Ce primeste `/cod-de-acces/verificare` prin `location.state`. */
export interface StareVerificareCod {
    identificator: string;
    cerutLa: number;
    mesaj: string;
    /**
     * De unde a plecat cererea de cod. Doar `'autentificare'` se scrie (din 21.09, ecranul
     * `/autentificare/email`): «Schimbă» se intoarce acolo. Lipsa = `/cod-de-acces`, ca inainte.
     */
    origine?: 'autentificare';
    /**
     * Scris doar de ecranele de inregistrare (`InregistrareCodPage`, `InregistrareElevCodPage`):
     * codul verificat incheie o inscriere, nu o logare. Lipsa = autentificare. Citit doar pentru
     * analytics (`otp_verified` / `otp_verify_failed` / `otp_resent`).
     */
    flux?: 'inregistrare';
    /**
     * Clipa (ms) de la care serverul mai trimite un cod, cand a spus-o el (429 `LIMITA_COD` pe
     * `/otp/inscriere`). Pana atunci «Trimite alt cod» ramane inactiv, cu numaratoare: o cerere
     * facuta mai devreme n-ar trimite nimic.
     */
    codNouDupa?: number;
}

/**
 * `state`-ul rutei `/cod-de-acces/verificare`, scris intr-un SINGUR loc.
 *
 * 🔴 Contractul e citit de `VerificareCodPage` (`identificator`, `cerutLa`, `mesaj` — altfel
 * ecranul cade pe conditionalul neutru, fara sa se planga nimic) si era scris in PATRU locuri:
 * `CerereCodPage`, `InregistrareCodPage`, `InregistrareElevCodPage` si ecranul de logare. Patru
 * literale care trebuie sa ramana identice nu raman identice; unul dintre ele se schimba si
 * rezultatul e o cheie citita de nimeni. Primele doua producatoare trec acum pe functia asta;
 * cele doua din wizard raman pe obiectul lor, deliberat — nu sunt ale acestei sesiuni.
 *
 * `acum` e PARAMETRU, nu `Date.now()` inauntru: altfel functia nu se poate testa (acelasi tipar
 * ca `stareRetrimitere(cerutLa, acum)` de deasupra).
 */
export function stareVerificareCod(
    identificator: string,
    mesaj: string,
    acum: number,
    origine?: 'autentificare',
    codNouDupa?: number,
): StareVerificareCod {
    return {
        identificator: identificator.trim(),
        cerutLa: acum,
        mesaj,
        ...(origine ? { origine } : {}),
        ...(typeof codNouDupa === 'number' && Number.isFinite(codNouDupa) ? { codNouDupa } : {}),
    };
}

/** Cifrele din text, fara separatori: tastatura numerica de Android trimite si asa ceva. */
function doarCifre(text: string): string {
    return String(text ?? '').replace(/\D+/g, '');
}

/**
 * Codul ca sir de casute. Intoarce mereu `LUNGIME_COD` pozitii, ca randarea sa nu
 * depinda de cate cifre a apucat omul sa scrie.
 */
export function casuteGoale(): string[] {
    return Array.from({ length: LUNGIME_COD }, () => '');
}

/** Pune o cifra intr-o caseta. Orice altceva decat o cifra goleste caseta. */
export function seteazaCifra(casute: string[], index: number, valoare: string): string[] {
    if (index < 0 || index >= LUNGIME_COD) return casute;
    const out = [...casute];
    out[index] = doarCifre(valoare).slice(-1);
    return out;
}

/**
 * Lipirea unui cod intreg (din mesaj, din clipboard, din autocompletarea
 * `one-time-code`). Umple de la pozitia curenta incolo si ignora restul: un text
 * lung n-are voie sa scrie in afara casetelor.
 */
export function lipeste(casute: string[], index: number, text: string): string[] {
    const cifre = doarCifre(text);
    if (!cifre) return casute;
    const start = Math.max(0, Math.min(index, LUNGIME_COD - 1));
    const out = [...casute];
    for (let i = 0; i < cifre.length && start + i < LUNGIME_COD; i++) {
        out[start + i] = cifre[i];
    }
    return out;
}

/** Ce a iesit dintr-o scriere intr-o caseta: casetele noi si caseta pe care se muta cursorul. */
export interface RezultatScriere {
    casute: string[];
    focus: number;
}

/**
 * Tot ce poate veni prin `onChange` / `onPaste` pe o caseta, intr-un singur loc.
 *
 * 🔴 Cazul care era gresit: o cifra tastata intr-o caseta DEJA PLINA. Browserul livreaza doua
 * caractere (cifra veche + cea noua, in ordinea cursorului), iar pana pe 2026-10-08 orice valoare
 * de doua caractere era tratata ca lipire — cifra veche ramanea pe loc si cea noua o SUPRASCRIA PE
 * URMATOAREA. Corectarea unei singure cifre strica doua. Acum cifra noua inlocuieste cifra casetei
 * si cursorul trece la caseta urmatoare.
 *
 * - o cifra (sau nimic): caseta primeste cifra / se goleste; cursorul merge la prima caseta goala;
 * - doua cifre intr-o caseta plina, una fiind cea veche: inlocuire, cursorul avanseaza;
 * - `LUNGIME_COD` cifre sau mai multe (codul intreg, lipit sau completat de `one-time-code`):
 *   primele `LUNGIME_COD` umplu casetele DE LA INCEPUT, oriunde ar fi cursorul — altfel un cod
 *   lipit in caseta a treia ar ramane fara ultimele doua cifre;
 * - orice altceva: lipire de la caseta curenta (`lipeste`).
 *
 * `eLipire` vine din `onPaste`: acolo doua cifre sunt chiar doua cifre lipite, nu „veche + noua",
 * deci regula de inlocuire nu se aplica.
 */
export function scrieInCaseta(casute: string[], index: number, valoare: string, eLipire = false): RezultatScriere {
    if (index < 0 || index >= LUNGIME_COD) return { casute, focus: Math.max(0, Math.min(index, LUNGIME_COD - 1)) };
    const cifre = doarCifre(valoare);
    const veche = doarCifre(casute[index] ?? '').slice(-1);
    // Un text lipit fara nicio cifra nu e o stergere: nu schimba nimic.
    if (eLipire && !cifre) return { casute, focus: index };

    if (!eLipire && veche && cifre.length === 2 && cifre.includes(veche)) {
        const noua = cifre[0] === veche ? cifre[1] : cifre[0];
        return { casute: seteazaCifra(casute, index, noua), focus: Math.min(index + 1, LUNGIME_COD - 1) };
    }

    const urmatoare = cifre.length >= LUNGIME_COD
        ? lipeste(casute, 0, cifre.slice(0, LUNGIME_COD))
        : cifre.length > 1 ? lipeste(casute, index, cifre) : seteazaCifra(casute, index, cifre);
    // Cursorul merge la prima caseta goala, nu automat la urmatoarea: dupa o lipire de sase
    // cifre, „urmatoarea" ar fi in afara.
    const goala = primaGoala(urmatoare);
    return { casute: urmatoare, focus: goala === -1 ? LUNGIME_COD - 1 : goala };
}

/** Codul scris, ca sir. Poate fi incomplet — apelantul verifica cu `esteComplet`. */
export function codScris(casute: string[]): string {
    return casute.map((c) => doarCifre(c).slice(-1)).join('');
}

/** Butonul se activeaza doar cand toate casetele sunt pline (macheta S1). */
export function esteComplet(casute: string[]): boolean {
    return codScris(casute).length === LUNGIME_COD;
}

/** Prima caseta goala; `-1` daca nu e niciuna. Acolo se muta cursorul. */
export function primaGoala(casute: string[]): number {
    return casute.findIndex((c) => !doarCifre(c));
}

/**
 * „m:ss", ca in textele S1 („Poți cere alt cod în 0:47").
 *
 * Se rotunjeste in SUS: la 0,4 secunde ramase inca mai e de asteptat, iar „0:00"
 * langa un buton inactiv arata a defect.
 */
export function formateazaSecunde(secunde: number): string {
    const s = Math.max(0, Math.ceil(Number.isFinite(secunde) ? secunde : 0));
    return Math.floor(s / 60) + ':' + String(s % 60).padStart(2, '0');
}

/**
 * Poate cere alt cod, si cat mai are de asteptat.
 *
 * Numaratoarea exista pentru ca un control inactiv FARA progres e cea mai proasta
 * varianta — aceeasi regula ca la poarta de video. `cerutLa` e clipa ultimei
 * cereri (ms), sau `null` daca nu s-a cerut inca niciuna. `codNouDupa` e clipa (ms) pana la care
 * serverul nu mai trimite coduri, cand a spus-o el.
 */
export function stareRetrimitere(
    cerutLa: number | null,
    acum: number,
    codNouDupa?: number | null,
): StareRetrimitere {
    if (!Number.isFinite(acum)) return { poateCere: true, secundeRamase: 0 };

    // Doua motive de asteptare, se ia cel mai lung: pauza noastra dupa ultima cerere si clipa
    // spusa de server (`codNouDupa`, la limita de coduri). Un ceas dat inapoi (sau o clipa a
    // cererii din viitor) nu are voie sa blocheze butonul pentru totdeauna: se trateaza ca „nu
    // mai e nimic de asteptat". `codNouDupa` e in viitor prin definitie, deci nu poate trece
    // prin garda aceea; in schimb o asteptare de peste o ora nu e una pe care serverul o cere
    // (fereastra limitei e de minute), deci o valoare stricata se ignora, nu blocheaza.
    const dupaPauza = cerutLa !== null && Number.isFinite(cerutLa) && cerutLa <= acum
        ? (cerutLa + PAUZA_SECUNDE * 1000 - acum) / 1000
        : 0;
    const pesteLimita = typeof codNouDupa === 'number' && Number.isFinite(codNouDupa)
        ? (codNouDupa - acum) / 1000
        : 0;
    const dupaLimita = pesteLimita > ASTEPTARE_MAXIMA_SECUNDE ? 0 : pesteLimita;
    const ramase = Math.max(dupaPauza, dupaLimita);
    return ramase > 0 ? { poateCere: false, secundeRamase: ramase } : { poateCere: true, secundeRamase: 0 };
}
